import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Divider,
  Tooltip,
  Avatar,
  LinearProgress,
  Alert,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNavigate } from "react-router-dom";
import api from "../api";

// ══════════════════════════════════════════════════════════════
// CONSTANTS
// ══════════════════════════════════════════════════════════════

const PRIMARY = "#005E60";
const DARK = "#1a1a2e";

const SOCIETY_COLORS = {
  DNV: "#003057",
  LR: "#c00000",
  ABS: "#1a5276",
  BV: "#1a3c6e",
  RINA: "#00843d",
  GL: "#005E60",
  NAVSEA: "#1a1a2e",
};

const STATUS_CONFIG = {
  active: { bg: "#d1e7dd", text: "#0a5233", label: "Active" },
  complete: { bg: "#cfe2ff", text: "#084298", label: "Complete" },
  on_hold: { bg: "#fff3cd", text: "#856404", label: "On Hold" },
};

const COMPONENT_COLORS = {
  VFD: "#005E60",
  TRANS: "#007b7d",
  MOTOR: "#00843d",
  GEN: "#c47a00",
  SWBDS: "#5c35a0",
  UPS: "#b00033",
  DBR: "#0077b6",
};

const COMPAT_CONFIG = {
  pass: {
    icon: <CheckCircleIcon sx={{ fontSize: 16, color: "#00843d" }} />,
    text: "Compatible",
    color: "#00843d",
  },
  fail: {
    icon: <ErrorIcon sx={{ fontSize: 16, color: "#b00033" }} />,
    text: "Issues Found",
    color: "#b00033",
  },
  warning: {
    icon: <WarningAmberIcon sx={{ fontSize: 16, color: "#c47a00" }} />,
    text: "Warnings",
    color: "#c47a00",
  },
  pending: {
    icon: <HourglassEmptyIcon sx={{ fontSize: 16, color: "#aaa" }} />,
    text: "Not Checked",
    color: "#888",
  },
};

// ══════════════════════════════════════════════════════════════
// STAT CARD
// ══════════════════════════════════════════════════════════════

function StatCard({ label, value, color }) {
  return (
    <Card
      elevation={0}
      sx={{
        textAlign: "center",
        padding: "1.2rem",
        borderTop: `4px solid ${color}`,
        borderRadius: "12px",
        border: "1px solid #e0e0e0",
      }}
    >
      <Typography variant="h3" sx={{ fontWeight: "bold", color }}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: "0.3rem" }}>
        {label}
      </Typography>
    </Card>
  );
}

// ══════════════════════════════════════════════════════════════
// DASHBOARD
// ══════════════════════════════════════════════════════════════

function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [deleting, setDeleting] = useState(null);
  const navigate = useNavigate();

  // ── Fetch projects ────────────────────────────────────────
  const fetchProjects = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/projects/");
      setProjects(res.data || []);
    } catch (err) {
      setError("Failed to load projects. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // ── Delete project ────────────────────────────────────────
  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (
      !window.confirm(
        "Delete this project and all its components? This cannot be undone.",
      )
    )
      return;
    setDeleting(id);
    try {
      await api.delete(`/projects/${id}`);
      setProjects((prev) => prev.filter((p) => p.id !== id));
    } catch {
      setError("Failed to delete project. Please try again.");
    } finally {
      setDeleting(null);
    }
  };

  // ── Filter projects ───────────────────────────────────────
  const filtered = projects.filter((p) => {
    const q = search.toLowerCase();
    return (
      p.project_number?.toLowerCase().includes(q) ||
      p.vessel_name?.toLowerCase().includes(q) ||
      p.customer_name?.toLowerCase().includes(q) ||
      p.classification_society?.toLowerCase().includes(q)
    );
  });

  // ── Summary stats ─────────────────────────────────────────
  const totalComponents = projects.reduce(
    (s, p) => s + (p.component_count || 0),
    0,
  );
  const totalESS = projects.reduce((s, p) => s + (p.ess_count || 0), 0);

  // ── Loading state ─────────────────────────────────────────
  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: "4rem" }}>
        <CircularProgress sx={{ color: PRIMARY }} />
      </Box>
    );

  return (
    <Box>
      {/* ── Header ─────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: "1.5rem",
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ color: DARK, fontWeight: "bold" }}>
            Projects
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage ship power system design projects
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchProjects} sx={{ color: PRIMARY }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/create")}
            sx={{
              backgroundColor: PRIMARY,
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: "bold",
              "&:hover": { backgroundColor: "#004749" },
            }}
          >
            New Project
          </Button>
        </Box>
      </Box>

      {/* ── Error banner ───────────────────────────────────── */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError("")}
          sx={{ mb: "1.5rem", borderRadius: "8px" }}
        >
          {error}
        </Alert>
      )}

      {/* ── Stats row ──────────────────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: "2rem" }}>
        {[
          { label: "Total Projects", value: projects.length, color: PRIMARY },
          {
            label: "Active",
            value: projects.filter((p) => p.status === "active").length,
            color: "#00843d",
          },
          {
            label: "Total Components",
            value: totalComponents,
            color: "#0077b6",
          },
          { label: "ESS Generated", value: totalESS, color: "#5c35a0" },
        ].map(({ label, value, color }) => (
          <Grid item xs={6} sm={3} key={label}>
            <StatCard label={label} value={value} color={color} />
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ mb: "1.5rem" }} />

      {/* ── Search ─────────────────────────────────────────── */}
      <TextField
        fullWidth
        placeholder="Search by project number, vessel, customer or classification society..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{
          mb: "1.5rem",
          backgroundColor: "white",
          borderRadius: "8px",
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            "&.Mui-focused fieldset": { borderColor: PRIMARY },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: PRIMARY }} />
            </InputAdornment>
          ),
        }}
      />

      {/* ── Empty state ────────────────────────────────────── */}
      {projects.length === 0 ? (
        <Card
          elevation={0}
          sx={{
            padding: "4rem",
            textAlign: "center",
            borderRadius: "12px",
            border: `2px dashed ${PRIMARY}`,
          }}
        >
          <FolderOpenIcon sx={{ fontSize: 64, color: "#ccc", mb: "1rem" }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No projects yet
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: "1.5rem" }}
          >
            Create your first ship power system project to get started
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/create")}
            sx={{
              backgroundColor: PRIMARY,
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: "bold",
            }}
          >
            Create First Project
          </Button>
        </Card>
      ) : filtered.length === 0 ? (
        <Card
          elevation={0}
          sx={{ padding: "2rem", textAlign: "center", borderRadius: "12px" }}
        >
          <Typography variant="h6" color="text.secondary">
            No results for "{search}"
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Try a different search term
          </Typography>
        </Card>
      ) : (
        /* ── Project cards grid ──────────────────────────── */
        <Grid container spacing={3}>
          {filtered.map((project) => {
            const status = STATUS_CONFIG[project.status || "active"];
            const societyColor =
              SOCIETY_COLORS[project.classification_society] || PRIMARY;
            const compCount = project.component_count || 0;
            const essCount = project.ess_count || 0;
            const essProgress =
              compCount > 0 ? Math.round((essCount / compCount) * 100) : 0;
            const compat = COMPAT_CONFIG[project.compat_status || "pending"];
            const types = project.component_types || [];

            return (
              <Grid item xs={12} sm={6} md={4} key={project.id}>
                <Card
                  elevation={0}
                  onClick={() => navigate(`/project/${project.id}`)}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "12px",
                    border: "1px solid #e0e0e0",
                    cursor: "pointer",
                    transition: "all 0.25s ease",
                    "&:hover": {
                      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                      transform: "translateY(-3px)",
                      borderColor: PRIMARY,
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    {/* Society avatar + status chip */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: "1rem",
                      }}
                    >
                      <Avatar
                        sx={{
                          backgroundColor: societyColor,
                          width: 44,
                          height: 44,
                          fontSize: "0.7rem",
                          fontWeight: "bold",
                        }}
                      >
                        {project.classification_society || "N/A"}
                      </Avatar>
                      <Chip
                        label={status?.label || "Active"}
                        size="small"
                        sx={{
                          backgroundColor: status?.bg,
                          color: status?.text,
                          fontWeight: "bold",
                          fontSize: "0.7rem",
                        }}
                      />
                    </Box>

                    {/* Project number */}
                    <Typography
                      variant="h6"
                      sx={{ color: DARK, fontWeight: "bold", mb: "0.3rem" }}
                    >
                      {project.project_number}
                    </Typography>

                    {/* Info box */}
                    <Box
                      sx={{
                        backgroundColor: "#f8f9fa",
                        borderRadius: "8px",
                        padding: "0.75rem",
                        mb: "0.8rem",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: "#555", mb: "0.2rem" }}
                      >
                        🚢 <strong>Vessel:</strong> {project.vessel_name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#555", mb: "0.2rem" }}
                      >
                        🏢 <strong>Customer:</strong> {project.customer_name}
                      </Typography>
                      {project.voltage_system && (
                        <Typography variant="body2" sx={{ color: "#555" }}>
                          ⚡ <strong>Voltage:</strong> {project.voltage_system}
                        </Typography>
                      )}
                      {project.frequency && (
                        <Typography variant="body2" sx={{ color: "#555" }}>
                          〰 <strong>Frequency:</strong> {project.frequency}
                        </Typography>
                      )}
                    </Box>

                    {/* Component type pills */}
                    {types.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                          mb: "0.75rem",
                        }}
                      >
                        {types.map((type) => (
                          <Chip
                            key={type}
                            label={type}
                            size="small"
                            sx={{
                              backgroundColor:
                                COMPONENT_COLORS[type] || PRIMARY,
                              color: "white",
                              fontWeight: "bold",
                              fontSize: "0.65rem",
                            }}
                          />
                        ))}
                      </Box>
                    )}

                    {/* Component count + compatibility */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: "0.5rem",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        🔧 <strong>{compCount}</strong> component
                        {compCount !== 1 ? "s" : ""}
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        {compat.icon}
                        <Typography
                          variant="caption"
                          sx={{ color: compat.color, fontWeight: "bold" }}
                        >
                          {compat.text}
                        </Typography>
                      </Box>
                    </Box>

                    {/* ESS progress bar */}
                    {compCount > 0 && (
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: "0.2rem",
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            ESS Progress
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: PRIMARY, fontWeight: "bold" }}
                          >
                            {essCount}/{compCount}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={essProgress}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: "#e0e0e0",
                            "& .MuiLinearProgress-bar": {
                              backgroundColor:
                                essProgress === 100 ? "#00843d" : PRIMARY,
                              borderRadius: 3,
                            },
                          }}
                        />
                      </Box>
                    )}

                    {/* Created date */}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: "0.75rem" }}
                    >
                      Created:{" "}
                      {new Date(project.created_at).toLocaleDateString(
                        "en-GB",
                        {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        },
                      )}
                    </Typography>
                  </CardContent>

                  <Divider />

                  <CardActions
                    sx={{
                      justifyContent: "space-between",
                      padding: "0.6rem 1rem",
                    }}
                  >
                    <Button
                      size="small"
                      startIcon={<FolderOpenIcon fontSize="small" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/project/${project.id}`);
                      }}
                      sx={{
                        color: PRIMARY,
                        fontWeight: "bold",
                        textTransform: "none",
                        "&:hover": { backgroundColor: "#e6f4f4" },
                      }}
                    >
                      Open Project
                    </Button>
                    <Tooltip title="Delete Project">
                      <span>
                        <IconButton
                          size="small"
                          onClick={(e) => handleDelete(e, project.id)}
                          disabled={deleting === project.id}
                          sx={{
                            color: "#b00033",
                            "&:hover": { backgroundColor: "#fce8ed" },
                          }}
                        >
                          {deleting === project.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}

export default Dashboard;
